import { Observable, of, Subject, BehaviorSubject, timer, throwError, identity } from 'rxjs';
import { mergeMap, tap, map, catchError, startWith, debounceTime, take, filter, share, distinctUntilChanged } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Pipe, Injectable, NgModule } from '@angular/core';
import * as i1$1 from 'primeng/progressbar';
import { ProgressBarModule } from 'primeng/progressbar';
import * as i1 from 'primeng/accordion';
import { AccordionModule } from 'primeng/accordion';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { mergeDeepRight } from 'ramda';
import * as i1$2 from '@angular/common/http';
import { MessageModule } from 'primeng/message';

/**
 * A custom RXJS operator that can be used to check if the current emission from an observable stream is the same as the previous emission. Unlike the standard distinctUntilChanged operator, this operator recursively evaluates arrays and objects to see if they match, even if the memory value isn't identical via "===".
 *
 * #### Limitations ####
 * - May be costly for large objects or arrays, use distinctUntilChanged with a custom comparator if that's the case
 * - Will not work for classes or complex objects with accessors or methods
 * @param source
 */
function smartDistinctUntilChanged() {
    let previousValue;
    return (source) => new Observable((observer) => {
        return source.subscribe({
            next: (value) => {
                if (!isEqual(value, previousValue)) {
                    observer.next(value);
                    // Check that structuredClone is available for deep cloning
                    if (isStructuredCloneAvailable()) {
                        previousValue = structuredClone(value);
                    }
                    else {
                        // Using parse/stringify works for simple examples but is problematic for things complex objects with methods or circular references
                        // Used for older browsers
                        previousValue = JSON.parse(JSON.stringify(value));
                    }
                }
            },
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
        });
    });
}
/**
 * Compares two values to see if they are equal. Supports primitive values, objects, and arrays.
 * @param a The first value to compare
 * @param b The second value to compare
 * @returns Returns true if the values are equal, false otherwise.
 */
const isEqual = (a, b) => {
    // Primitives, memory value matches or nil value matches
    if (a === b)
        return true;
    // Nill non matches
    if (a == null || b == null || a == undefined || b == undefined)
        return false;
    // Objects
    if (typeof a === 'object' && typeof b === 'object') {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length)
            return false;
        // Iterate through all the keys, and compare each key's value
        for (let i = 0; i < aKeys.length; i++) {
            const key = aKeys[i]; // as keyof typeof a;
            // Check if the key exists in both objects
            if (!Object.prototype.hasOwnProperty.call(b !== null && b !== void 0 ? b : {}, key))
                return false;
            // Recursively compare the values of the current key
            if (!isEqual(a[key], b[key]))
                return false;
        }
        return true;
        // Arrays
    }
    else if (Array.isArray(a) && Array.isArray(b)) {
        // Check array length
        if (a.length !== b.length)
            return false;
        // Loop through the array and compare all nested values
        for (let i = 0; i < a.length; i++) {
            if (!isEqual(a[i], b[i]))
                return false;
        }
        return true;
    }
    return false;
};
/** Node JS check for SSR */
const isNode$1 = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
const isBrowser$1 = !isNode$1;
/** Check if isStructuredCloneAvailable is available in the API */
const isStructuredCloneAvailable = () => {
    return (isBrowser$1 &&
        typeof window !== 'undefined' &&
        typeof window.postMessage === 'function' &&
        typeof window.Worker !== 'undefined');
};

/**
 * TODO Items:
 * - Support option where only non-nil results are cached
 * - Add concatMap like throttling to preserve order of requests?
 * - Support a localstorage option. Will need a method to clear localstorage too
 */
/** A list of ID's to clear cache */
let cacheClearIDList = [];
/**
 * Manually clear the cache that has the corresponding cacheID set in options
 *
 * The cache will not be cleared immediately but only on a subsequent emission
 * @param cacheId
 * @returns
 */
const cacheMapClear = (cacheId) => {
    if (!cacheId) {
        return;
    }
    cacheClearIDList.push(cacheId);
};
/**
 * Caches an observable stream in memory. Works exactly like mergeMap but uses the upstream data source to cache data returned from the downstream observable.
 * Be conscientious of memory management
 *
 * Prefers the upstream data to be a primitive. Non primitives will be stringified which may still work but likely not reliably
 *
 * @param source
 */
const cacheMap = (dest, options) => (source) => {
    /** Store cached data */
    let cache = {};
    return source.pipe(mergeMap((s) => {
        // Clear cacheID is set and the cacheID is queued to be cleared
        if ((options === null || options === void 0 ? void 0 : options.cacheId) && cacheClearIDList.includes(options.cacheId)) {
            // Clear cache
            cache = {};
            // Remove ID from cache list
            cacheClearIDList = cacheClearIDList.filter((id) => id !== options.cacheId);
        }
        // Generate a uniqueID from the source
        // If custom function supplied, use that
        const uniqueId = (options === null || options === void 0 ? void 0 : options.uniqueIdFn) ? String(options.uniqueIdFn(s)) : generateUniqueId(s);
        // Get current time
        const now = new Date();
        // Get cache item for typesafety
        const cacheItem = cache[uniqueId];
        // Check if current time is greater than cache time, if so clear cache item
        if ((cacheItem === null || cacheItem === void 0 ? void 0 : cacheItem.expiry) && now.getTime() > cacheItem.expiry) {
            cache = Object.assign({}, cache);
            delete cache[uniqueId];
        }
        // If found in the cache, return from cache
        else if (cache[uniqueId]) {
            // console.log('from cache', uniqueId);
            return of(cache[uniqueId].data);
        }
        // Return data from stream instead of cache
        return dest(s).pipe(
        // After child operator emits, store result in the cache
        tap((result) => {
            // console.log('from stream', uniqueId);
            // Check if TTL is specified, if so set expiry
            const payload = (options === null || options === void 0 ? void 0 : options.ttl)
                ? { data: result, expiry: now.getTime() + options.ttl * 1000 }
                : { data: result };
            cache = Object.assign({}, cache, { [uniqueId]: payload });
        }));
    }));
};
/**
 * Generate a unique ID from an unknown input
 * @param value
 * @returns
 */
const generateUniqueId = (value) => {
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

/**
 * Intercept an http request and return a state object
 * @param source
 */
const httpRequestWithState = () => (source) => {
    const state = {
        loading: true,
        modifying: false,
        errorModify: null,
        error: null,
        data: false,
    };
    return source.pipe(map((x) => Object.assign({}, state, { loading: false, error: null, data: x })), catchError((err) => of(Object.assign({}, state, { loading: false, error: err }))), startWith(state));
};

/**
 * A map function that works directly on the data property preserving the api state wrapper
 * @param source
 */
const mapData = (fn) => (source) => {
    return source.pipe(map((state) => Object.assign({}, state, { data: fn(state.data) })));
};

const initialState = {
    loading: false,
    modifying: false,
    error: null,
    errorModify: null,
    data: false,
};
const initialEntityState = Object.assign(Object.assign({}, initialState), { entities: {}, ids: [], data: null });

const keysApi = Object.keys(initialState).sort();
const keysEntity = Object.keys(initialEntityState).sort();
/**
 * Node JS check for SSR
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNode = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
const isBrowser = !isNode;
/**
 * Typeguard for actions, checks action and ensures payload is properly typed
 * @param action
 * @param actionCreator
 * @returns
 */
const isActionType$1 = (action, actionCreator) => {
    return action.type === actionCreator.type;
};
/**
 * Is this action an api action
 * @param action
 * @returns
 */
const isActionApi = (action) => !!action.storeId;
/**
 * Typeguard for checking if the input is an api state type
 * @param obj
 */
const isApiState = (obj) => {
    // If null or not an object
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return false;
    }
    const entityKeys = Object.keys(obj).sort();
    const isMatch = true;
    for (let i = 0; i < keysApi.length; i++) {
        const key = keysApi[i];
        if (!entityKeys.includes(key)) {
            return false;
        }
    }
    return isMatch;
};
/**
 * Typeguard for checking if the input is an entity state type
 * @param obj
 */
const isEntityState = (obj) => {
    // If null or not an object
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return false;
    }
    const entityKeys = Object.keys(obj).sort();
    const isMatch = true;
    for (let i = 0; i < keysEntity.length; i++) {
        const key = keysEntity[i];
        if (!entityKeys.includes(key)) {
            return false;
        }
    }
    return isMatch;
};

/**
 * Combines the domain state from multiple entity stores into a single state object.
 * Useful when joining the data from multiple stores into a single stream
 * If multiple errors, will only keep the first error in sequence
 * @param states
 * @param data
 */
const NtsCombineEntityState = (states) => {
    const state = {
        loading: false,
        modifying: false,
        error: null,
        errorModify: null,
        data: false,
    };
    // If state is nil
    if (!states) {
        return state;
    }
    // Ensure states is always an array for reduce
    const statesArray = !Array.isArray(states) ? [states] : states;
    // Filter out any non-entity states in array
    const statesEntity = statesArray.filter(isApiState);
    // Roll up the separate entity states into a single state object
    if (statesEntity && statesEntity.length) {
        state.loading = statesEntity.reduce((a, b) => a || b.loading, false);
        state.modifying = statesEntity.reduce((a, b) => a || b.modifying, false);
        state.error = statesEntity.reduce((a, b) => a || b.error, null);
        state.errorModify = statesEntity.reduce((a, b) => a || b.errorModify, null);
        state.data = statesEntity.reduce((a, b) => (b.data === undefined || b.data === null || a === false ? false : a), true);
    }
    return state;
};

class NtsErrorComponent {
    constructor() {
        /** Will the close error button appear in the corner */
        this.canClose = true;
        /** By default is the full error shown? */
        this.errorExpanded = false;
        /** Show error details in accordian */
        this.errorShowDetails = true;
    }
    ngOnInit() { }
}
NtsErrorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
NtsErrorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: NtsErrorComponent, selector: "nts-error", inputs: { error: "error", canClose: "canClose", errorExpanded: "errorExpanded", errorShowDetails: "errorShowDetails" }, ngImport: i0, template: "<div class=\"p-message p-message-error p-corner-all w-100 mb-3\" *ngIf=\"error\" id=\"nts-error\">\r\n  <button type=\"button\" class=\"close float-end\" aria-label=\"Close\" *ngIf=\"canClose\" (click)=\"error = null\">\r\n    <span aria-hidden=\"true\">&times;</span>\r\n  </button>\r\n\r\n  <!-- Transclude the error message, default to span if none specified-->\r\n\r\n  <div class=\"p-2\">\r\n    <div #ref>\r\n      <ng-content></ng-content>\r\n    </div>\r\n\r\n    <div *ngIf=\"!ref.innerHTML.trim()\">\r\n      <i class=\"fas fa-times\"></i>\r\n      {{ error.message || 'An error has occured.' }}\r\n    </div>\r\n\r\n    <div class=\"mt-3\" *ngIf=\"errorShowDetails\">\r\n      <!-- Show accordion not error on load-->\r\n\r\n      <p-accordion>\r\n        <p-accordionTab header=\"See error details\">\r\n          <pre class=\"mb-0\">{{ error | json }}</pre>\r\n        </p-accordionTab>\r\n      </p-accordion>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: ["#nts-error pre{padding:.5rem;background:#ffeeef}#nts-error .p-accordion .p-accordion-header{margin-bottom:0}#nts-error .p-accordion .p-accordion-header a{background-color:#ffeeef;border:#f8d7da;color:#fff;color:#721c24;font-weight:400}#nts-error .p-accordion .p-accordion-content{background-color:#ffeeef;border:0}#nts-error .p-accordion .p-accordion-toggle-icon{color:#721c24!important}\n"], components: [{ type: i1.Accordion, selector: "p-accordion", inputs: ["multiple", "style", "styleClass", "expandIcon", "collapseIcon", "activeIndex"], outputs: ["onClose", "onOpen", "activeIndexChange"] }, { type: i1.AccordionTab, selector: "p-accordionTab", inputs: ["header", "disabled", "cache", "transitionOptions", "selected"], outputs: ["selectedChange"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "json": i2.JsonPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nts-error', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div class=\"p-message p-message-error p-corner-all w-100 mb-3\" *ngIf=\"error\" id=\"nts-error\">\r\n  <button type=\"button\" class=\"close float-end\" aria-label=\"Close\" *ngIf=\"canClose\" (click)=\"error = null\">\r\n    <span aria-hidden=\"true\">&times;</span>\r\n  </button>\r\n\r\n  <!-- Transclude the error message, default to span if none specified-->\r\n\r\n  <div class=\"p-2\">\r\n    <div #ref>\r\n      <ng-content></ng-content>\r\n    </div>\r\n\r\n    <div *ngIf=\"!ref.innerHTML.trim()\">\r\n      <i class=\"fas fa-times\"></i>\r\n      {{ error.message || 'An error has occured.' }}\r\n    </div>\r\n\r\n    <div class=\"mt-3\" *ngIf=\"errorShowDetails\">\r\n      <!-- Show accordion not error on load-->\r\n\r\n      <p-accordion>\r\n        <p-accordionTab header=\"See error details\">\r\n          <pre class=\"mb-0\">{{ error | json }}</pre>\r\n        </p-accordionTab>\r\n      </p-accordion>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: ["#nts-error pre{padding:.5rem;background:#ffeeef}#nts-error .p-accordion .p-accordion-header{margin-bottom:0}#nts-error .p-accordion .p-accordion-header a{background-color:#ffeeef;border:#f8d7da;color:#fff;color:#721c24;font-weight:400}#nts-error .p-accordion .p-accordion-content{background-color:#ffeeef;border:0}#nts-error .p-accordion .p-accordion-toggle-icon{color:#721c24!important}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { error: [{
                type: Input
            }], canClose: [{
                type: Input
            }], errorExpanded: [{
                type: Input
            }], errorShowDetails: [{
                type: Input
            }] } });

class NtsDomainStateComponent {
    constructor() {
        /** Should the state component look the modify state instead of load state */
        this.modify = false;
        /** Should this component show loading state? */
        this.showLoading = true;
        /** Should this component show modifying state? */
        this.showModifying = true;
        /** Should this component show loading error */
        this.showErrorLoading = true;
        /** Should this component show modifying error? */
        this.showErrorModifying = true;
    }
    /** Default domain state */
    set state(state) {
        this.stateSrc = NtsCombineEntityState(state);
    }
}
NtsDomainStateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsDomainStateComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
NtsDomainStateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: NtsDomainStateComponent, selector: "nts-api-state", inputs: { state: "state", modify: "modify", showLoading: "showLoading", showModifying: "showModifying", showErrorLoading: "showErrorLoading", showErrorModifying: "showErrorModifying" }, ngImport: i0, template: "<div class=\"nts-state\">\r\n  <!-- Loading, no data -->\r\n  <ng-container *ngIf=\"!stateSrc || (showLoading && stateSrc?.loading && !stateSrc?.data)\">\r\n    <p-progressBar mode=\"indeterminate\" class=\"nts-state-spacer\" [style]=\"{ height: '12px' }\"></p-progressBar>\r\n  </ng-container>\r\n\r\n  <!-- Loading with data OR modifying -->\r\n  <ng-container *ngIf=\"(showLoading && stateSrc?.loading && stateSrc?.data) || (showModifying && stateSrc?.modifying)\">\r\n    <p-progressBar mode=\"indeterminate\" class=\"nts-state-spacer\" [style]=\"{ height: '2px' }\"></p-progressBar>\r\n  </ng-container>\r\n\r\n  <!-- Loading error -->\r\n  <ng-container *ngIf=\"showLoading && showErrorLoading && stateSrc?.error\">\r\n    <nts-error [error]=\"stateSrc?.error\" [canClose]=\"false\"></nts-error>\r\n  </ng-container>\r\n\r\n  <!-- Modifying error -->\r\n  <ng-container *ngIf=\"showModifying && showErrorModifying && stateSrc?.errorModify && !stateSrc?.error\">\r\n    <nts-error [error]=\"stateSrc?.errorModify\" [canClose]=\"false\"></nts-error>\r\n  </ng-container>\r\n\r\n  <!-- Content from parent -->\r\n  <ng-content *ngIf=\"stateSrc?.data !== null\"></ng-content>\r\n</div>\r\n", styles: [".nts-state{position:relative}.nts-state .nts-state-spacer{position:absolute;top:0;z-index:25;width:100%}.nts-state .p-progressbar{background:rgba(234,234,234,.7)}\n"], components: [{ type: i1$1.ProgressBar, selector: "p-progressBar", inputs: ["value", "showValue", "style", "styleClass", "unit", "mode"] }, { type: NtsErrorComponent, selector: "nts-error", inputs: ["error", "canClose", "errorExpanded", "errorShowDetails"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsDomainStateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nts-api-state', encapsulation: ViewEncapsulation.None, template: "<div class=\"nts-state\">\r\n  <!-- Loading, no data -->\r\n  <ng-container *ngIf=\"!stateSrc || (showLoading && stateSrc?.loading && !stateSrc?.data)\">\r\n    <p-progressBar mode=\"indeterminate\" class=\"nts-state-spacer\" [style]=\"{ height: '12px' }\"></p-progressBar>\r\n  </ng-container>\r\n\r\n  <!-- Loading with data OR modifying -->\r\n  <ng-container *ngIf=\"(showLoading && stateSrc?.loading && stateSrc?.data) || (showModifying && stateSrc?.modifying)\">\r\n    <p-progressBar mode=\"indeterminate\" class=\"nts-state-spacer\" [style]=\"{ height: '2px' }\"></p-progressBar>\r\n  </ng-container>\r\n\r\n  <!-- Loading error -->\r\n  <ng-container *ngIf=\"showLoading && showErrorLoading && stateSrc?.error\">\r\n    <nts-error [error]=\"stateSrc?.error\" [canClose]=\"false\"></nts-error>\r\n  </ng-container>\r\n\r\n  <!-- Modifying error -->\r\n  <ng-container *ngIf=\"showModifying && showErrorModifying && stateSrc?.errorModify && !stateSrc?.error\">\r\n    <nts-error [error]=\"stateSrc?.errorModify\" [canClose]=\"false\"></nts-error>\r\n  </ng-container>\r\n\r\n  <!-- Content from parent -->\r\n  <ng-content *ngIf=\"stateSrc?.data !== null\"></ng-content>\r\n</div>\r\n", styles: [".nts-state{position:relative}.nts-state .nts-state-spacer{position:absolute;top:0;z-index:25;width:100%}.nts-state .p-progressbar{background:rgba(234,234,234,.7)}\n"] }]
        }], propDecorators: { state: [{
                type: Input
            }], modify: [{
                type: Input
            }], showLoading: [{
                type: Input
            }], showModifying: [{
                type: Input
            }], showErrorLoading: [{
                type: Input
            }], showErrorModifying: [{
                type: Input
            }] } });

/**
 * Extracts the data property out of an entity state. Most useful for working with multi typed arrays with combinations
 * of entity and non entity data types since it will only return the response data not state data
 * TODO: Figure out how the heck to type this
 */
class EntityData {
    /**
     *
     * @param value - Any data type. This pipe will combine data from entity and non entity states supplied
     * @param allowPartial - Allow data through if undefined. If false will only emit data when all supplied data is not undefined
     */
    transform(value) {
        // return null;
        if (value === null || value === undefined) {
            return value;
        }
        // If not an array, return either the source data or the entity state data property
        if (!Array.isArray(value)) {
            return isApiState(value) ? value.data : value;
        }
        // If array, extract data from entitystate, otherwise just grab source data
        return value.map((d) => (isApiState(d) ? d.data : d));
    }
}
EntityData.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityData, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EntityData.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityData, name: "data" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityData, decorators: [{
            type: Pipe,
            args: [{
                    name: 'data',
                }]
        }] });

/**
 * Determines if the source observable containing multiple entity states has data loaded for each of them
 * Pretty much only useful for multi-entity state arrays or combine arrays of entity and non-entity states
 * TODO: Figure out how the heck to type this
 */
class EntityIsLoaded {
    /**
     *
     * @param value - Any data type. This pipe will combine data from entity and non entity states supplied
     * @param allowPartial - Allow data through if undefined. If false will only emit data when all supplied data is not undefined
     */
    transform(value) {
        // return null;
        if (value === null || value === undefined) {
            return false;
        }
        // If not an array, return either the source data or the entity state data property
        if (!Array.isArray(value)) {
            return isApiState(value) && value.data ? true : false;
        }
        return value
            .filter((d) => isApiState(d))
            .reduce((a, b) => (b.data === undefined || a === false ? false : a), true);
    }
}
EntityIsLoaded.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityIsLoaded, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EntityIsLoaded.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityIsLoaded, name: "isLoaded" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: EntityIsLoaded, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isLoaded',
                }]
        }] });

class NtsBaseStore {
    constructor() {
        this.events$ = NtsBaseStore._events$.pipe();
    }
    dispatch(a) {
        NtsBaseStore._events$.next(a);
    }
}
NtsBaseStore._events$ = new Subject();
/**
 * Base store instance shared by all stores
 * @returns
 */
const ntsStore = new NtsBaseStore();

var ApiActions;
(function (ApiActions) {
    ApiActions["REFRESH"] = "REFRESH";
    ApiActions["RESET"] = "RESET";
    ApiActions["GET"] = "GET";
    ApiActions["REQUEST"] = "REQUEST";
    ApiActions["POST"] = "POST";
    ApiActions["PUT"] = "PUT";
    ApiActions["PATCH"] = "PATCH";
    ApiActions["DELETE"] = "DELETE";
})(ApiActions || (ApiActions = {}));
var ApiEvents;
(function (ApiEvents) {
    ApiEvents["GET_START"] = "GET_START";
    ApiEvents["GET_SUCCESS"] = "GET_SUCCESS";
    ApiEvents["GET_ERROR"] = "GET_ERROR";
    ApiEvents["MODIFY_START"] = "MODIFY_START";
    ApiEvents["MODIFY_SUCCESS"] = "MODIFY_SUCCESS";
    ApiEvents["MODIFY_ERROR"] = "MODIFY_ERROR";
})(ApiEvents || (ApiEvents = {}));
var StoreTypes;
(function (StoreTypes) {
    StoreTypes["ALL"] = "ALL";
    StoreTypes["API_ONLY"] = "API_ONLY";
    StoreTypes["UI_ONLY"] = "UI_ONLY";
})(StoreTypes || (StoreTypes = {}));
var UIActions;
(function (UIActions) {
})(UIActions || (UIActions = {}));
var UIEvents;
(function (UIEvents) {
})(UIEvents || (UIEvents = {}));

function mergeConfig(c1, c2) {
    return Object.assign(Object.assign({ disableAppendId: Object.assign(Object.assign({}, c1.disableAppendId), c2.disableAppendId), map: Object.assign(Object.assign({}, c1.map), c2.map) }, c1), c2);
}
/**
 * Deep merge config objects
 * @param c1
 * @param c2
 * @returns

export const mergeConfig = (c1: any, c2: any): any => ({
  disableAppendId: {
    ...c1.disableAppendId,
    ...c2.disableAppendId,
  },
  map: {
    ...c1.map,
    ...c2.map,
  },
  ...c1,
  ...c2,
});
 */
/**
 * Typeguards
 */
const is = {
    entityConfig: (c) => c.uniqueId ? true : false,
    callbackFn: (c) => typeof c === 'function',
};
/**
 * Merge the response from the api with the payload depending on their various types
 * @param data2Api Data sent to the web api
 * @param dataFromApi Data returned from the web api
 */
const mergePayloadWithApiResponse = (data2Api, dataFromApi, uniqueId) => {
    // If api response is nil
    if (dataFromApi === null || dataFromApi === undefined) {
        return data2Api;
        // API response is STRING and payload is OBJECT
        // Assume response is a guid/id, update payload with guid/id
    }
    else if (typeof dataFromApi === 'string' && typeof data2Api === 'object' && !Array.isArray(data2Api) && uniqueId) {
        return Object.assign(Object.assign({}, data2Api), { [uniqueId]: dataFromApi });
        // API response is OBJECT and payload is OBJECT
        // Perform deep merge with data from api taking priority
    }
    else if (typeof dataFromApi === 'object' &&
        !Array.isArray(dataFromApi) &&
        typeof data2Api === 'object' &&
        !Array.isArray(data2Api)) {
        // Perform shallow merge
        return Object.assign(Object.assign({}, data2Api), dataFromApi);
    }
    else {
        console.error(`Api response or payload does not have a matched condition. Payload is a ${typeof data2Api} and response is ${typeof data2Api}`, data2Api, data2Api);
    }
    // Return payload if no conditions matched
    return data2Api;
};
/**
 * Convert array to record
 * @param arr
 * @param uniqueId
 * @returns

 */
const arrayToRecord = (arr, uniqueId) => arr.reduce((a, b) => (Object.assign(Object.assign({}, a), { [b[uniqueId]]: b })), {});
/**
 *
 * @param storeDataSrc
 * @param payloadSrc
 * @param uniqueId
 * @returns
 */
const mergeDedupeArrays = (storeDataSrc, payloadSrc, uniqueId) => {
    // Ensure array types
    const storeData = !Array.isArray(storeDataSrc) ? [storeDataSrc] : storeDataSrc;
    const payload = !Array.isArray(payloadSrc) ? [payloadSrc] : payloadSrc;
    // Convert both types to records
    const storeRecord = arrayToRecord(storeData, uniqueId);
    const payloadRecord = arrayToRecord(payload, uniqueId);
    const entitiesMerged = mergeDeepRight(storeRecord, payloadRecord);
    // Get a list of NEW entities that do not exist in the source array
    const entitiesNew = payload.filter((e) => (!storeRecord[e[uniqueId]] ? true : false));
    // Return mapped array with existing entities updated and new entities appended
    // Preserves array order of original
    return {
        data: [...storeData.map((e) => entitiesMerged[e[uniqueId]]), ...entitiesNew],
        entities: entitiesMerged,
    };
};
/**
 * Delete an entity or array of entities from the source array
 * @param storeDataSrc
 * @param payloadSrc
 * @param uniqueId
 * @returns
 */
const deleteEntities = (storeDataSrc, payloadSrc, uniqueId) => {
    // Ensure array types
    const storeData = !Array.isArray(storeDataSrc) ? [storeDataSrc] : storeDataSrc;
    const payload = !Array.isArray(payloadSrc) ? [payloadSrc] : payloadSrc;
    // Convert both types to records
    const storeRecord = arrayToRecord(storeData, uniqueId);
    payload.forEach((e) => {
        const key = e[uniqueId];
        if (storeRecord[key]) {
            delete storeRecord[key];
        }
    });
    return {
        data: [...storeData.map((e) => storeRecord[e[uniqueId]]).filter((e) => !!e)],
        entities: storeRecord,
    };
};
/**
 * Create the correct url to use based on the config, rest verb type and entity
 * @param config
 * @param verb
 * @param e
 * @returns
 */
const apiUrlGet = (config, verb, e) => {
    var _a, _b, _c;
    if (!config.apiUrl) {
        console.error('Please define an apiUrl');
        return '/';
    }
    // Get default api URL
    let apiUrl = config.apiUrl;
    // If the api type is a function, execute it against the entity provided
    if (is.callbackFn(apiUrl)) {
        apiUrl = apiUrl(e);
    }
    // If prepend url is specified
    if (config.apiUrlBase) {
        apiUrl = config.apiUrlBase + apiUrl;
    }
    // If PUT/PATCH/DELETE, append unique ID provided it's not disabled
    if (is.entityConfig(config) &&
        ((verb === 'put' && ((_a = config.disableAppendId) === null || _a === void 0 ? void 0 : _a.put) !== true && config.uniqueId && e && !Array.isArray(e)) ||
            (verb === 'patch' && ((_b = config.disableAppendId) === null || _b === void 0 ? void 0 : _b.patch) !== true && config.uniqueId && e && !Array.isArray(e)) ||
            (verb === 'delete' && ((_c = config.disableAppendId) === null || _c === void 0 ? void 0 : _c.delete) !== true && config.uniqueId && e && !Array.isArray(e)))) {
        apiUrl = apiUrl + '/' + e[config.uniqueId];
    }
    // If append url is specified
    if (config.apiUrlAppend) {
        apiUrl = apiUrl + config.apiUrlAppend;
    }
    // If a custom override url was specified for this verb
    // Note that override replaces all previous settings like prepent/append etc
    if (!!(config === null || config === void 0 ? void 0 : config.apiUrlOverride) && !!(config === null || config === void 0 ? void 0 : config.apiUrlOverride[verb])) {
        const urlOverride = config.apiUrlOverride[verb] || '';
        // Check if the override is a string or a callback function that needs to return a string
        if (typeof urlOverride === 'string') {
            apiUrl = urlOverride;
        }
        else {
            apiUrl = urlOverride(e);
        }
    }
    return apiUrl;
};
/**
 * Should the store refresh data for an autoload?
 *
 * Conditions:
 * - Data property is null, ie no data
 * - Error from previous get request
 * - API response was an empty array or empty object
 * @param s
 * @returns
 */
const canRefreshStoreData = (s) => {
    return s.data === null || s.error ? true : false;
    /**
     * Future State or configurable option, autoreload of empty array or object
     else if (typeof s.data === 'object' && Array.isArray(s.data) && !s.data.length) {
      return true;
    } else if (typeof s.data === 'object' && !Array.isArray(s.data) && !Object.keys(s.data).length) {
      return true;
    }
     */
};

/**
 * TODO:
 * - Make fresh API call on error
 */
/**
 * Automatically create an api store to manage interaction between a local flux store and a remote api
 */
class NtsApiStoreCreator extends NtsBaseStore {
    constructor(http, config, isEntityStore = true) {
        super();
        this.http = http;
        this.config = config;
        this.isEntityStore = isEntityStore;
        /** Store state object */
        this.state = this.isEntityStore
            ? this.getStateEntitySrc
            : this.getStateSrc;
        /** Returns both the api state and data */
        this._state$ = new BehaviorSubject(this.state);
        /**
         * Holds both the store data and api state in a single subscription
         */
        this.state$ = this._state$.pipe(debounceTime(1), 
        // Autoload data if a subscriber is present and no data is already in store
        // Will reload data if last request was an error or data in store is nill or empty array/object
        tap((s) => {
            if (this.config.autoLoad !== false && canRefreshStoreData(s) && this.autoloadInProgress === false) {
                this.autoloadInProgress = true; // Set autoload in progress
                this.get()
                    .pipe(take(1))
                    .subscribe(
                // After completing API call, set in progress to false. Prevents infinite refresh loop
                () => timer(1)
                    .pipe(take(1))
                    .subscribe(() => (this.autoloadInProgress = false)), () => timer(1)
                    .pipe(take(1))
                    .subscribe(() => (this.autoloadInProgress = false)));
            }
        }));
        /** Is autoload request in flight */
        this.autoloadInProgress = false;
        /** Listen to events being broadcast, handle events sent to this store or all stores */
        this.events$ = NtsApiStoreCreator._events$.pipe(filter((a) => isActionApi(a) &&
            (a.storeId === this.config.storeId || a.storeId === StoreTypes.ALL || a.storeId === StoreTypes.API_ONLY)), tap((a) => {
            // Add typeguard for api actions
            if (!isActionApi(a)) {
                return;
            }
            switch (a.type) {
                // Refresh data in store
                case ApiActions.GET:
                    this.get(a.options).subscribe();
                    break;
                // Perform POST request
                case ApiActions.POST:
                    this.post(a.payload, a.options).subscribe();
                    break;
                // Perform PUT request
                case ApiActions.PUT:
                    this.put(a.payload, a.options).subscribe();
                    break;
                // Perform PATCH request
                case ApiActions.PATCH:
                    this.patch(a.payload, a.options).subscribe();
                    break;
                // Perform DELETE request
                case ApiActions.DELETE:
                    this.delete(a.payload, a.options).subscribe();
                    break;
                // Refresh data in store
                case ApiActions.REFRESH:
                    this.refresh(a.options).subscribe();
                    break;
                // Reset store
                case ApiActions.RESET:
                    this.reset();
                    break;
            }
        }));
        /** Store a shared reference to the http get request so it can be canceled and shared */
        this.httpGet$ = this.http.get(apiUrlGet(this.config, 'get', null));
    }
    /** Default non-entity API state */
    get getStateSrc() {
        return Object.assign({}, {
            loading: false,
            modifying: false,
            error: false,
            errorModify: false,
            data: null,
        });
    }
    /** Default entity API state */
    get getStateEntitySrc() {
        return Object.assign({}, {
            loading: false,
            modifying: false,
            error: false,
            errorModify: false,
            data: null,
            entities: {},
        });
    }
    /**
     * Perform a get request to load data into the store
     * @param optionsSrct
     */
    get(optionsOverride = {}) {
        return this._get(optionsOverride);
    }
    /**
     * Request is a POST operation that functions a GET. A payload body is passed and the response is loaded into the store
     *
     * Useful for things like search requests that need parameters not in a query string
     * @param payload
     * @param optionsOverride
     */
    request(payload, optionsOverride) {
        return this._get(Object.assign({ refresh: true }, optionsOverride), payload);
    }
    /**
     * Perform a get request to load data into the store
     * @param optionsOverride
     * @param postPayload
     */
    _get(optionsOverride = {}, postPayload) {
        const options = mergeConfig(this.config, optionsOverride);
        // If data is null or refresh cache is requested, otherwise default to cache
        if ((this.state.data === null || options.refresh || !this.httpGet$) && !this.state.loading) {
            const url = apiUrlGet(options, 'get', null);
            // Set loading to true, reset  errors
            this.stateChange({ loading: true, error: null });
            // Dispatch event to the global scope
            if (this.config.storeId) {
                this.dispatch({ type: ApiEvents.GET_START, storeId: this.config.storeId, payload: null });
            }
            // Is this a GET request or a POST that functions as a GET
            // Some data request require a post body
            // TODO: Fix any
            const httpRequest = postPayload ? this.http.post(url, postPayload) : this.http.get(url);
            this.httpGet$ = httpRequest.pipe(
            // Handle api success
            tap((r) => {
                // Map api response if requested
                const result = this.config.map && this.config.map.get ? this.config.map.get(r) : r;
                const state = { loading: false, data: result, errorModify: null };
                let entities = null;
                // Check if this api response has entities, create entity property
                const config = this.config; // Run through typeguard so it doesn't need to be typechecked again in the reduce
                if (this.isEntityStore && is.entityConfig(config) && config.uniqueId && Array.isArray(result)) {
                    entities = result.reduce((a, b) => (Object.assign(Object.assign({}, a), { [b[String(config.uniqueId)]]: b })), {});
                    state.entities = entities;
                }
                // Update state
                this.stateChange(state);
                // Dispatch event to the global scope
                if (this.config.storeId) {
                    this.dispatch({ type: ApiEvents.GET_SUCCESS, storeId: this.config.storeId, payload: r });
                }
            }), 
            // Handle api errors
            catchError((err) => {
                // Update state
                this.stateChange({ loading: false, error: err });
                if (this.config.storeId) {
                    this.dispatch({ type: ApiEvents.GET_ERROR, storeId: this.config.storeId, payload: err });
                }
                return throwError(err);
            }), take(1), // Ensure http request only fires once since the memory reference is stored
            share());
        }
        return this.httpGet$;
    }
    /**
     * Perform a POST request
     * @param data
     * @param optionsOverride
     * @returns
     */
    post(data, optionsOverride = {}) {
        var _a;
        const options = mergeConfig(this.config, optionsOverride);
        const url = apiUrlGet(options, 'post', null);
        return this.upsert(this.http.post(url, data), data, (_a = this.config.map) === null || _a === void 0 ? void 0 : _a.post);
    }
    /**
     * Perform a PUT request
     * @param data
     * @param optionsOverride
     * @returns
     */
    put(data, optionsOverride = {}) {
        var _a;
        const options = mergeConfig(this.config, optionsOverride);
        const url = apiUrlGet(options, 'put', data);
        return this.upsert(this.http.put(url, data), data, (_a = this.config.map) === null || _a === void 0 ? void 0 : _a.put);
    }
    /**
     * Perform a PATCH request
     * @param data
     * @param optionsOverride
     * @returns
     */
    patch(data, optionsOverride = {}) {
        var _a;
        const options = mergeConfig(this.config, optionsOverride);
        const url = apiUrlGet(options, 'patch', data);
        return this.upsert(this.http.patch(url, data), data, (_a = this.config.map) === null || _a === void 0 ? void 0 : _a.patch);
    }
    /**
     * Perform a DELETE request
     * @param data
     * @param optionsOverride
     * @returns
     */
    delete(data, optionsOverride = {}) {
        const options = mergeConfig(this.config, optionsOverride);
        const url = apiUrlGet(options, 'delete', data);
        // Reset state
        this.stateChange({ modifying: true, errorModify: null });
        return this.http.delete(url).pipe(
        // Most deletes shouldn't have a return but sometimes it's necessary
        tap((r) => {
            var _a;
            // Check if this is an entity store
            if (this.isEntityStore &&
                is.entityConfig(this.config) &&
                !!((_a = this.state) === null || _a === void 0 ? void 0 : _a.data) &&
                Array.isArray(this.state.data)) {
                // Delete entities from store
                const updated = deleteEntities(this.state.data, data, this.config.uniqueId);
                this.stateChange(Object.assign({ modifying: false }, updated));
            }
            else {
                // Delete on a non entity format
                this.stateChange({ modifying: false, data: r || null });
            }
        }), 
        // Handle error
        catchError((err) => {
            // Update state
            this.stateChange({ modifying: false, errorModify: err });
            return throwError(err);
        }));
    }
    /**
     * Flatten all PUT/POST/PATCH requests into an upsert for simplicity
     * @param apiRequest
     * @param data
     * @returns
     */
    upsert(apiRequest, data, mapFn) {
        // Reset state
        this.stateChange({ modifying: true, errorModify: null });
        // Dispatch event to the global scope
        if (this.config.storeId) {
            this.dispatch({ type: ApiEvents.MODIFY_START, storeId: this.config.storeId, payload: null });
        }
        // Make api request
        return apiRequest.pipe(
        // Handle success
        tap((r) => {
            var _a;
            // If a map function was provided, modify the data before executing anything else
            const resMapped = mapFn ? mapFn(r) : r;
            // Merge the api response with the payload
            const resMerged = mergePayloadWithApiResponse(data, resMapped);
            // If this is an entity store
            if (this.isEntityStore &&
                is.entityConfig(this.config) &&
                !!((_a = this.state) === null || _a === void 0 ? void 0 : _a.data) &&
                Array.isArray(this.state.data)) {
                // TODO: Fix any
                const delta = mergeDedupeArrays(this.state.data, resMerged, this.config.uniqueId);
                this.stateChange(Object.assign({ modifying: false }, delta));
            }
            else {
                this.stateChange({ modifying: false, resMerged });
            }
            // Dispatch event to the global scope
            if (this.config.storeId) {
                this.dispatch({ type: ApiEvents.MODIFY_SUCCESS, storeId: this.config.storeId, payload: r });
            }
        }), 
        // Handle error
        catchError((err) => {
            // Update state
            this.stateChange({ modifying: false, errorModify: err });
            if (this.config.storeId) {
                this.dispatch({ type: ApiEvents.MODIFY_ERROR, storeId: this.config.storeId, payload: err });
            }
            return throwError(err);
        }));
    }
    /**
     * Refresh the data in the store
     */
    refresh(optionsOverride = {}) {
        return this.get(Object.assign(Object.assign({}, optionsOverride), { refresh: true }));
    }
    /**
     * Reset store to its initial state
     */
    reset() {
        // Set autoload in progress flag to prevent active subscribers from immediately reloading store data when reset is performed
        this.autoloadInProgress = true;
        this.stateChange(this.isEntityStore ? this.getStateEntitySrc : this.getStateSrc);
        // Reset flag so that next sub will reload store data
        timer(1)
            .pipe(take(1))
            .subscribe(() => (this.autoloadInProgress = false));
    }
    /**
     * Perform updates to the base state object, shallow only
     * @param state
     */
    stateChange(state) {
        this.state = Object.assign({}, this.state, state);
        this._state$.next(this.state);
    }
}

/**
 * Create an instance of a UI store
 */
class NtsUIStoreCreator extends NtsBaseStore {
    constructor(initialState, options) {
        var _a;
        super();
        this.initialState = initialState;
        this.options = options;
        /** Observable of store state */
        this.state$ = new BehaviorSubject(Object.assign({}, this.initialState));
        // If persistID specified, rehydrate store state from localStorage
        if (isBrowser && ((_a = this.options) === null || _a === void 0 ? void 0 : _a.persistId)) {
            const state = localStorage.getItem(this.options.persistId);
            if (state) {
                this.update(JSON.parse(state));
            }
        }
    }
    select$(k, options) {
        return this.state$.pipe(
        // Is this a function or string to pull out data from the model
        map((s) => (typeof k === 'function' ? k(s) : s[k])), 
        // Disable distinctUntilChanged if requested in the options
        (options === null || options === void 0 ? void 0 : options.disableDistinct) ? identity : distinctUntilChanged());
    }
    update(value) {
        var _a;
        if (typeof value === 'function') {
            this.state$.pipe(take(1)).subscribe((state) => {
                const n = value(state);
                this.stateChange(n);
            });
        }
        else {
            this.stateChange(value);
        }
        // If persistId is specified, save all state changes to localStorage
        if (isBrowser && ((_a = this.options) === null || _a === void 0 ? void 0 : _a.persistId)) {
            this.state$
                .pipe(take(1))
                .subscribe((state) => { var _a; return localStorage.setItem(((_a = this.options) === null || _a === void 0 ? void 0 : _a.persistId) || '', JSON.stringify(state)); });
        }
        return this.state$.pipe(take(1));
    }
    /**
     * Reset store to it's initial state
     */
    reset() {
        this.stateChange(Object.assign({}, this.initialState));
    }
    /**
     * Update state of UI store
     * @param update
     */
    stateChange(update) {
        this.state$.pipe(take(1)).subscribe((state) => this.state$.next(Object.assign(Object.assign({}, state), update)));
    }
}
/**
 * Create an instance of a UI store
 */
const ntsUIStoreCreator = (initialState, options) => new NtsUIStoreCreator(initialState, options);

const configSrc = {
    uniqueId: 'guid',
};
/**
 * Create an instance of an entity store
 */
class NtsEntityStore extends NtsApiStoreCreator {
    constructor(http, config) {
        super(http, Object.assign(configSrc, config), true);
        this.config = config;
        /** Select an array of all the entities in the store. Does not include state. */
        this.selectAll$ = this.state$.pipe(map((s) => s.data), distinctUntilChanged());
        /** Modify the store data with the supplied callback function while preserving state information.
         *
         * Useful for mapping or filtering data in the store while still maintaining access to state.  */
        this.stateSelect$ = (fn) => this.state$.pipe(map((state) => {
            const data = state.data ? fn([...state.data]) : state.data;
            const entities = data
                ? data.reduce((a, b) => (Object.assign(Object.assign({}, a), { [b[String(this.config.uniqueId)]]: b })), {})
                : {};
            return Object.assign({}, state, { data: data, entities: entities });
        }));
        /** Select a single entity from the store. Does not include state. */
        this.selectOne$ = (uniqueId) => this.state$.pipe(map((s) => (!!s.entities && !!s.entities[uniqueId] ? s.entities[uniqueId] : null)), distinctUntilChanged());
        /**
         * Select a or modify subset of data from the store. Does not include state.
         * Pass a callback function that modifies the data in the store property
         */
        this.select$ = (fn) => this.state$.pipe(map((s) => fn(s.data ? [...s.data] : s.data)), distinctUntilChanged());
    }
}

class NtsApiStore extends NtsApiStoreCreator {
    constructor(http, config) {
        super(http, config, false);
        /**
         * Select a smaller subset of data from the store
         */
        this.select$ = this.state$.pipe(map((s) => s.data), distinctUntilChanged());
    }
}

/**
 * Create a curried instance of the api store creator
 *
 * @example
 * private store = ntsApiStoreCreator(this.http, { apiUrlBase: '//jsonplaceholder.typicode.com' })
 * @param http A reference to Angular's http service
 * @param configBase Default configuration for all store instances used by this creator. Will be overwritten by individual store properties
 * @returns
 */
const ntsApiStoreCreator = (http, configBase) => {
    // @ts-ignore : TODO: Resolve overload typings issue. Maybe :/
    const store = (config, isEntityStore = true) => isEntityStore
        ? new NtsEntityStore(http, mergeConfig(configBase || {}, config))
        : new NtsApiStore(http, mergeConfig(configBase || {}, config));
    return store;
};

class NtsStateManagementService {
    constructor(http) {
        this.http = http;
        /** Listen to global events */
        this.events$ = ntsStore.events$;
        /** Store references to all created stores  */
        this.storeRefs = [];
        /**
         * Create a curried instance of the api store creator
         * @param configBase Default configuration for all store instances used by this creator. Will be overwritten by individual store properties
         * @example
         * private store = this.storeCreator({ apiUrlBase: '//jsonplaceholder.typicode.com' });
         * // An entity store
         * public users = this.store<Models.User>({ uniqueId: 'id', storeId: 'users', apiUrl: '/users' });
         * // A non-entity store
         * public post = this.store<Models.Post>({ apiUrl: '/posts/1' });
         * @returns
         */
        this.createBaseStore = ((configBase) => (config) => {
            // Merge base config with specific store config
            const c = config ? mergeConfig(configBase, config) : config;
            // If a uniqueID is specified, return an entity store. If not return an api store
            return is.entityConfig(c) ? this.createEntityStore(c) : this.createApiStore(c);
        });
        /**
         * Create an entity based api store
         * @param config Configuration for this store
         * @returns
         */
        this.createEntityStore = (config) => {
            const store = new NtsEntityStore(this.http, config);
            this.storeRefs = [
                ...this.storeRefs,
                { type: 'api', storeId: (config === null || config === void 0 ? void 0 : config.storeId) || String(Math.floor(Math.random() * 10000000)), ref: store },
            ];
            return store;
        };
        /**
         * Create a non-entity based api store
         * @param config Configuration for this store
         * @returns
         */
        this.createApiStore = (config) => {
            const store = new NtsApiStore(this.http, config);
            this.storeRefs = [
                ...this.storeRefs,
                { type: 'api', storeId: (config === null || config === void 0 ? void 0 : config.storeId) || String(Math.floor(Math.random() * 10000000)), ref: store },
            ];
            return store;
        };
        /**
         *
         * @param initialState
         * @param options
         * @returns
         */
        this.createUIStore = (initialState, options) => {
            const store = new NtsUIStoreCreator(initialState, options);
            this.storeRefs = [
                ...this.storeRefs,
                { type: 'ui', storeId: (options === null || options === void 0 ? void 0 : options.persistId) || String(Math.floor(Math.random() * 10000000)), ref: store },
            ];
            return store;
        };
    }
    /**
     * Reset stores created by this service, can be API, UI or all
     * This is useful for clearing data on auth failures
     */
    resetStores(type = 'all') {
        // If all stores, return all. If only a specific type, filter out wrong types
        const stores = type === 'all' ? this.storeRefs : this.storeRefs.filter((s) => s.type === type);
        // Reset stores
        stores.forEach((s) => s.ref.reset());
    }
    /**
     * Dispatch an action to all stores
     */
    dispatch(action) {
        ntsStore.dispatch(action);
    }
}
NtsStateManagementService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementService, deps: [{ token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
NtsStateManagementService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }]; } });

/**
 * Typeguard for actions, checks action and ensures payload is properly typed
 * @example
 * if (isActionType(action, actionCreator)) {
 *      console.log(action.payload);
 * }
 * @param action
 * @param actionCreator
 * @returns
 */
const isActionType = (action, actionCreator) => {
    return action.type === actionCreator.type;
};
/**
 * Easily create type safe action creators
 * @example
 * // Create an action creator
 * const guidChanged = actionCreator<string>('GUID_CHANGE');
 * // Create an action with a payload
 * const action = guidChanged('12345');
 * // Use built in typeguard to enforce type safety
 * if (guidChanged.match(action)) {
 *      console.log(action.payload); // '12345'
 * }
 * @returns
 */
const actionCreator = (type) => Object.assign((payload) => {
    const action = {
        type: type,
        payload: payload,
    };
    return action;
}, {
    type: type,
    match: (action) => action.type === type,
});
/**
 * USAGE EXAMPLES
 */
// Create a new file that will be easily importable by the stores that need to consume it such as
// /shared/stores/store.actions.ts
// Import the actionCreator
// import { actionCreator } from '@ntersol/state-management';
/**
// Create and export an actions creator dictionary
export const actions = {
  // Initialize an action creator. An action needs a unique value and the type to associate with that
  tokenChanged: actionCreator<string>('TOKEN_CHANGED'),
  isLoggedIn: actionCreator<boolean>('IS_LOGGED_IN'),
  // activeUser: actionCreator<Models.User>('ACTIVE_USER'),
}

// OR create a standalone action creator. An action needs a unique enumerated value and the type to associate with that
export const tokenChangedAction = actionCreator<string>('TOKEN_CHANGED');

// Now create an action with a specific payload
// This will create an instance  of the token changed action with the associated string
const action = actions.tokenChanged('12345');


if (isActionType(action, actions.tokenChanged)) {
  console.log(action.payload); // '12345'
}

if (tokenChangedAction.match(action)) {
  console.log(action.payload); // '12345'
}
 */

const Components = [NtsDomainStateComponent, NtsErrorComponent, EntityData, EntityIsLoaded];
/**
 * State management tools intended to work with Akita
 */
class NtsStateManagementModule {
}
NtsStateManagementModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NtsStateManagementModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementModule, declarations: [NtsDomainStateComponent, NtsErrorComponent, EntityData, EntityIsLoaded], imports: [CommonModule, AccordionModule, ProgressBarModule, MessageModule], exports: [NtsDomainStateComponent, NtsErrorComponent, EntityData, EntityIsLoaded] });
NtsStateManagementModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementModule, imports: [[CommonModule, AccordionModule, ProgressBarModule, MessageModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: NtsStateManagementModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [Components],
                    imports: [CommonModule, AccordionModule, ProgressBarModule, MessageModule],
                    exports: [Components],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ApiActions, ApiEvents, EntityData, EntityIsLoaded, NtsApiStoreCreator, NtsBaseStore, NtsDomainStateComponent, NtsErrorComponent, NtsStateManagementModule, NtsStateManagementService, NtsUIStoreCreator, StoreTypes, UIActions, UIEvents, actionCreator, apiUrlGet, arrayToRecord, cacheMap, cacheMapClear, canRefreshStoreData, deleteEntities, httpRequestWithState, is, isActionType, isBrowser$1 as isBrowser, isNode$1 as isNode, mapData, mergeConfig, mergeDedupeArrays, mergePayloadWithApiResponse, ntsApiStoreCreator, ntsStore, ntsUIStoreCreator, smartDistinctUntilChanged };
